import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Building, MapPin, Pencil, Phone, Plus } from 'lucide-react'
import { departmentName, formatPhone } from '@tumin/domain'
import { EmptyState, ErrorState } from '@/components/empty-state'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { BranchDialog, type Branch } from '@/features/admin/branches/branch-dialog'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query-keys'
import { requirePermission } from '@/lib/route-guards'
import { useShortcuts, type ShortcutDef } from '@/lib/shortcuts'

export const Route = createFileRoute('/_app/admin/branches')({
  beforeLoad: requirePermission('catalog:manage'),
  component: BranchesPage,
})

/** Tarjetas esqueleto mientras cargan las agencias. */
const SKELETON_CARDS = 3

function BranchesPage() {
  const [editing, setEditing] = useState<Branch | null>(null)
  const [open, setOpen] = useState(false)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.branches.all,
    queryFn: ({ signal }) => api.get<Branch[]>('/branches', undefined, signal),
  })

  const openNew = () => {
    setEditing(null)
    setOpen(true)
  }
  const shortcuts = useMemo<ShortcutDef[]>(
    () => [{ id: 'branch-new', group: 'Acciones', keys: ['N'], sequence: ['n'], label: 'Nueva agencia', run: openNew }],
    [],
  )
  useShortcuts(shortcuts)

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Administración"
        title="Agencias"
        description="Oficinas de atención, su ubicación y sus asuetos locales."
        actions={
          <Button onClick={openNew}>
            <Plus /> Nueva agencia
          </Button>
        }
      />
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: SKELETON_CARDS }).map((_, i) => (
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-xl border border-border bg-card">
          <ErrorState error={error} onRetry={() => void refetch()} />
        </div>
      ) : !data?.length ? (
        <div className="rounded-xl border border-dashed border-border bg-card">
          <EmptyState
            icon={Building}
            title="Aún no hay agencias"
            description="Registre al menos una agencia: los usuarios, clientes y cajas se asignan a ella."
            action={
              <Button onClick={openNew}>
                <Plus /> Crear la primera agencia
              </Button>
            }
          />
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((branch) => (
            <li key={branch.id} className="flex flex-col rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="tabular text-xs font-semibold text-muted-foreground">{branch.code}</p>
                  <h2 className="truncate text-base font-semibold">{branch.name}</h2>
                </div>
                <StatusBadge kind="active" value={String(branch.isActive)} />
              </div>
              <div className="mt-3 flex-1 space-y-1.5 text-sm text-muted-foreground">
                <p className="flex gap-2">
                  <MapPin className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>
                    {branch.address}, {branch.municipality}, {departmentName(branch.department) ?? branch.department}
                  </span>
                </p>
                {branch.phone && (
                  <p className="flex gap-2">
                    <Phone className="mt-0.5 size-4 shrink-0" aria-hidden />
                    <span className="tabular">{formatPhone(branch.phone)}</span>
                  </p>
                )}
                <p className="text-xs">
                  {branch.localHolidays.length === 0 ? 'Sin asuetos locales' : `${String(branch.localHolidays.length)} asueto(s) local(es)`}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 w-fit"
                onClick={() => {
                  setEditing(branch)
                  setOpen(true)
                }}
              >
                <Pencil /> Editar
              </Button>
            </li>
          ))}
        </ul>
      )}
      <BranchDialog open={open} onOpenChange={setOpen} branch={editing} />
    </div>
  )
}
